import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal, viewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { s25Resource } from "../../util/s25.resource";
import { SeSecurityStore } from "./se.security.store";
import { AlertService } from "../../services/alert.service";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
import { S25States } from "../../services/state/state.map";
import { S25TableModule } from "../s25-table/s25.table.module";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { Table } from "../s25-table/Table";
import { GenericTableSimpleSelectComponent } from "../s25-table/generics/generic.table.simple.select.component";
import {
    ACCT_RATING_RULE_CODES,
    AcctRating,
    AcctRatingRule,
    AcctRatingRuleCode,
    AcctRatingRuleMode,
    changedAcctRatingRules,
    SeOrgStatusService,
} from "./se.org.status.service";
import { AcctRatingWarnMessageChange, SeOrgStatusWarnMessagesComponent } from "./se.org.status.warn.messages.component";
import { S25Util } from "../../util/s25-util";

type ModeOption = { value: AcctRatingRuleMode; label: string };

const ALL_MODE_OPTIONS: ModeOption[] = [
    { value: "ALLOW", label: "Allow" },
    { value: "WARN", label: "Warn" },
    { value: "DENY", label: "Deny" },
];

@TypeManagerDecorator("s25-ng-se-org-status-admin")
@Component({
    selector: "s25-ng-se-org-status-admin",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25RouterLinkDirective, S25TableModule],
    template: `
        @if (securityStore.canConfigureOrgStatus()) {
            <div class="se-org-status-admin c-padding--double">
                <h2>Rating profiles</h2>
                <p class="c-margin-bottom--double">
                    Configure action rules for each organization status. Manage status labels in
                    <a
                        s25-ng-router-link
                        [stateName]="ratingsStateName"
                        [stateParams]="ratingsNavigationParams"
                        [options]="ratingsNavigationOptions"
                        >Org Ratings</a
                    >.
                </p>

                <div class="se-org-status-admin__legend c-margin-bottom--single">
                    @for (ruleEntry of ACCT_RATING_RULE_CODES; track ruleEntry.code) {
                        <div class="se-org-status-admin__legend-item">
                            <strong>{{ ruleEntry.label }}</strong>
                            <span class="ngFinePrint c-margin-left--half">{{ ruleEntry.help }}</span>
                        </div>
                    }
                </div>

                <div class="se-org-status-admin__toolbar c-margin-bottom--single">
                    <button
                        type="button"
                        class="aw-button aw-button--primary"
                        [disabled]="saving() || !isDirty()"
                        (click)="saveAll()"
                    >
                        {{ saving() ? "Saving…" : "Save all" }}
                    </button>
                    <button
                        type="button"
                        class="aw-button aw-button--outline c-margin-left--single"
                        [disabled]="saving() || !isDirty()"
                        (click)="cancel()"
                    >
                        Cancel
                    </button>
                </div>

                <div class="se-org-status-admin__table-wrap">
                    <s25-ng-table
                        #statusTable
                        [dataSource]="tableDataSource()"
                        [hasExpandableRows]="true"
                        [hasRefresh]="true"
                        [showHeaderWhenNoData]="true"
                        [noResultsMessage]="'No organization statuses configured.'"
                        [unlimitedWidth]="true"
                    />
                </div>
            </div>
        }
    `,
    styles: `
        :host {
            display: block;
        }

        .se-org-status-admin__table-wrap {
            overflow-x: auto;
        }

        .se-org-status-admin__legend-item {
            margin-bottom: 0.35rem;
        }

        .se-org-status-admin__toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
        }

        .se-org-status-admin__dirty-hint {
            color: var(--color--text-muted);
        }
    `,
})
export class SeOrgStatusAdminComponent {
    protected readonly securityStore = inject(SeSecurityStore);
    protected readonly ACCT_RATING_RULE_CODES = ACCT_RATING_RULE_CODES;

    readonly ratingsStateName = S25States.names.ratings;
    readonly ratingsNavigationParams = { resetAll: true } as S25States.ParamsForName<"ratings">;
    readonly ratingsNavigationOptions = { reload: true };
    readonly saving = signal(false);

    readonly statusTable = viewChild(S25TableComponent);

    readonly configsResource = s25Resource({
        loader: () => SeOrgStatusService.loadConfigs(),
    });

    readonly draftRatings = linkedSignal<AcctRating[]>(() => {
        const ratings = this.configsResource.value();
        return ratings ? structuredClone(ratings) : [];
    });

    readonly savedSnapshot = computed(() => {
        const ratings = this.configsResource.value();
        return ratings ? this.serializeRatings(ratings) : "";
    });

    readonly isDirty = computed(() => {
        if (!this.configsResource.value()) {
            return false;
        }
        return this.serializeRatings(this.draftRatings()) !== this.savedSnapshot();
    });

    readonly tableDataSource = computed((): Table.DataSource => {
        const columns: Table.Column[] = [{ id: "status", header: "Status", minWidth: 140, permanent: true }];
        for (const ruleEntry of ACCT_RATING_RULE_CODES) {
            columns.push({ id: ruleEntry.code, header: ruleEntry.label, minWidth: 150 });
        }

        return {
            type: "unpaginated",
            dataSource: async (query) => {
                if (query.forceRefresh) {
                    await this.configsResource.reloadAndAwait();
                } else {
                    await this.waitForConfigsResource();
                }

                const loadError = this.configsResource.error();
                if (loadError) {
                    throw loadError;
                }

                return { rows: this.buildTableRows() };
            },
            columns,
        };
    });

    readonly ruleLabelByCode = computed((): Partial<Record<AcctRatingRuleCode, string>> => {
        const labels: Partial<Record<AcctRatingRuleCode, string>> = {};
        for (const entry of ACCT_RATING_RULE_CODES) {
            labels[entry.code] = entry.label;
        }
        return labels;
    });

    ratingHasWarnMessages(rating: AcctRating): boolean {
        return (rating.rules ?? []).some((rule) => rule.modeCode === "WARN");
    }

    warnRulesFor(rating: AcctRating): AcctRatingRule[] {
        return (rating.rules ?? []).filter((rule) => rule.modeCode === "WARN");
    }

    cancel(): void {
        const snapshot = this.savedSnapshot();
        if (!snapshot) {
            return;
        }
        const restoredRatings = structuredClone(JSON.parse(snapshot) as AcctRating[]);
        this.draftRatings.set(restoredRatings);
        void this.statusTable()?.refresh(false, false, true);
    }

    async saveAll(): Promise<void> {
        if (!this.isDirty() || this.saving()) {
            return;
        }
        this.saving.set(true);
        try {
            const savedRatings = JSON.parse(this.savedSnapshot()) as AcctRating[];
            const changedRules = changedAcctRatingRules(this.draftRatings(), savedRatings);
            if (!changedRules.length) {
                return;
            }
            await SeOrgStatusService.saveConfigs(changedRules);
            await this.configsResource.reloadAndAwait();
            await this.statusTable()?.refresh(false, false, true);
        } catch (caughtError) {
            console.error(caughtError);
            await AlertService.alert(
                S25Util.errorText(caughtError, "Could not save organization status configuration."),
            );
        } finally {
            this.saving.set(false);
        }
    }

    setRuleMode(acctRatingId: number, ruleCode: AcctRatingRuleCode, modeCode: AcctRatingRuleMode): void {
        this.patchRating(acctRatingId, (rating) => {
            const rule = this.ensureRule(rating, ruleCode);
            rule.modeCode = modeCode;
            if (modeCode !== "WARN") {
                rule.message = undefined;
            }
        });
    }

    setRuleMessage(acctRatingId: number, ruleCode: AcctRatingRuleCode, message: string): void {
        this.draftRatings.update((ratings) =>
            ratings.map((rating) => {
                if (rating.acctRatingId !== acctRatingId) {
                    return rating;
                }
                this.ensureRule(rating, ruleCode).message = message;
                return rating;
            }),
        );
    }

    private async waitForConfigsResource(): Promise<void> {
        const waitTimeoutMs = 120_000;
        const deadlineMs = performance.now() + waitTimeoutMs;
        while (this.configsResource.isLoading()) {
            if (performance.now() > deadlineMs) {
                throw new Error("Timed out waiting for organization status configuration");
            }
            await new Promise<void>((resolve) => setTimeout(resolve, 50));
        }
    }

    private buildTableRows(): Table.Row[] {
        const labelByCode = this.ruleLabelByCode();
        return this.draftRatings().map((rating) => {
            const cells: Table.Row["cells"] = {
                status: { text: rating.ratingName },
            };
            for (const ruleEntry of ACCT_RATING_RULE_CODES) {
                const ruleCode = ruleEntry.code;
                const modeCode = this.ruleMode(rating, ruleCode);
                cells[ruleCode] = {
                    component: GenericTableSimpleSelectComponent,
                    inputs: {
                        modelValue: modeCode,
                        options: ALL_MODE_OPTIONS.map((option) => ({
                            value: option.value,
                            label: option.label,
                        })),
                    },
                    outputs: {
                        modelValueChange: (nextMode: AcctRatingRuleMode) => {
                            this.setRuleMode(rating.acctRatingId, ruleCode, nextMode);
                            const table = this.statusTable();
                            void table?.refresh(false, false, true).then(() => {
                                if (nextMode === "WARN") {
                                    table?.expandRow(rating.acctRatingId);
                                }
                            });
                        },
                    },
                    textValue: modeCode,
                };
            }

            const tableRow: Table.Row = {
                id: rating.acctRatingId,
                name: rating.ratingName,
                data: rating,
                cells,
                expandable: this.ratingHasWarnMessages(rating),
            };

            if (tableRow.expandable) {
                tableRow.expandedContent = {
                    component: SeOrgStatusWarnMessagesComponent,
                    inputs: {
                        acctRatingId: rating.acctRatingId,
                        warnRules: this.warnRulesFor(rating),
                        ruleLabelByCode: labelByCode,
                    },
                    outputs: {
                        messageChange: (change: AcctRatingWarnMessageChange) => {
                            this.setRuleMessage(change.acctRatingId, change.ruleCode, change.message);
                        },
                    },
                };
            }

            return tableRow;
        });
    }

    private serializeRatings(ratings: AcctRating[]): string {
        return JSON.stringify(ratings);
    }

    private ruleMode(rating: AcctRating, ruleCode: AcctRatingRuleCode): AcctRatingRuleMode {
        return this.findRule(rating, ruleCode)?.modeCode ?? "ALLOW";
    }

    private patchRating(acctRatingId: number, patch: (rating: AcctRating) => void): void {
        this.draftRatings.update((ratings) =>
            ratings.map((rating) => {
                if (rating.acctRatingId !== acctRatingId) {
                    return rating;
                }
                const copy = structuredClone(rating);
                patch(copy);
                return copy;
            }),
        );
    }

    private findRule(rating: AcctRating, ruleCode: AcctRatingRuleCode): AcctRatingRule | undefined {
        return rating.rules?.find((rule) => rule.ruleCode === ruleCode);
    }

    private ensureRule(rating: AcctRating, ruleCode: AcctRatingRuleCode): AcctRatingRule {
        if (!rating.rules) {
            rating.rules = [];
        }
        let rule = rating.rules.find((entry) => entry.ruleCode === ruleCode);
        if (!rule) {
            rule = { acctRatingId: rating.acctRatingId, ruleCode, modeCode: "ALLOW" };
            rating.rules.push(rule);
        }
        rule.acctRatingId = rating.acctRatingId;
        return rule;
    }
}
